import clsx from 'clsx';
import css from 'dom-helpers/style';
import React from 'react';
import PropTypes from 'prop-types';
import Transition, { EXITED, ENTERED, ENTERING, EXITING } from 'react-transition-group/Transition';
import capitalize from '../bootstrap/utils/capitalize';
import createChainedFunction from '../bootstrap/utils/createChainedFunction';
const MARGINS = {
  height: ['marginTop', 'marginBottom'],
  width: ['marginLeft', 'marginRight']
};

// reading a dimension prop will cause the browser to recalculate,
// which will let our animations work
function triggerBrowserReflow(node) {
  node.offsetHeight; // eslint-disable-line no-unused-expressions
}
function getDimensionValue(dimension, elem) {
  const value = elem[`offset${capitalize(dimension)}`];
  const margins = MARGINS[dimension];
  return value + parseInt(css(elem, margins[0]), 10) + parseInt(css(elem, margins[1]), 10);
}
const collapseStyles = {
  [EXITED]: 'collapse',
  [EXITING]: 'collapsing',
  [ENTERING]: 'collapsing',
  [ENTERED]: 'collapse in'
};
class Collapse extends React.Component {
  constructor() {
    super(...arguments);
    /* -- Expanding -- */
    this.handleEnter = elem => {
      elem.style[this.getDimension()] = '0';
    };
    this.handleEntering = elem => {
      const dimension = this.getDimension();
      elem.style[dimension] = this._getScrollDimensionValue(elem, dimension);
    };
    this.handleEntered = elem => {
      elem.style[this.getDimension()] = null;
    };
    /* -- Collapsing -- */
    this.handleExit = elem => {
      const dimension = this.getDimension();
      elem.style[dimension] = `${this.props.getDimensionValue(dimension, elem)}px`;
      triggerBrowserReflow(elem);
    };
    this.handleExiting = elem => {
      elem.style[this.getDimension()] = '0';
    };
  }
  getDimension() {
    return typeof this.props.dimension === 'function' ? this.props.dimension() : this.props.dimension;
  }
  // for testing
  _getScrollDimensionValue(elem, dimension) {
    return `${elem[`scroll${capitalize(dimension)}`]}px`;
  }
  render() {
    const {
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      className,
      children,
      ...props
    } = this.props;
    delete props.dimension;
    delete props.getDimensionValue;
    const handleEnter = createChainedFunction(this.handleEnter, onEnter);
    const handleEntering = createChainedFunction(this.handleEntering, onEntering);
    const handleEntered = createChainedFunction(this.handleEntered, onEntered);
    const handleExit = createChainedFunction(this.handleExit, onExit);
    const handleExiting = createChainedFunction(this.handleExiting, onExiting);
    return /*#__PURE__*/React.createElement(Transition, Object.assign({}, props, {
      "aria-expanded": props.role ? props.in : null,
      onEnter: handleEnter,
      onEntering: handleEntering,
      onEntered: handleEntered,
      onExit: handleExit,
      onExiting: handleExiting
    }), (state, innerProps) => /*#__PURE__*/React.cloneElement(children, {
      ...innerProps,
      className: clsx(className, children.props.className, collapseStyles[state], this.getDimension() === 'width' && 'width')
    }));
  }
}
Collapse.defaultProps = {
  in: false,
  timeout: 300,
  mountOnEnter: false,
  unmountOnExit: false,
  appear: false,
  dimension: 'height',
  getDimensionValue
};
export default Collapse;